import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useMemberHomeActivities } from '@/hooks/useMemberHomeActivities';
import { OutingCard } from '@/components/outings/OutingCard';
import { useFavorites } from '@/components/favorites/FavoritesProvider';
import { computeDistanceLabel, pickPrimaryBadge } from '@/lib/outings';
import { Skeleton } from '@/components/ui/skeleton';
import { track } from '@/lib/analytics';
import type { OutingTag } from '@/types/outings';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { formatEventTimingSummary } from '@/lib/events/eventCardUtils';

const DB_TAG_TO_UI: Record<string, OutingTag> = {
  barnvanlig: 'barnvänligt',
  plocka_sjalv: 'plocka-själv',
  kultur: 'kultur',
};

export function ActivitiesSection() {
  const { data: activities, isLoading } = useMemberHomeActivities('Dalarna', 3);
  const { isFavorited, toggleFavorite } = useFavorites();

  if (isLoading) {
    return (
      <section>
        <h2 className="text-sm font-medium text-muted-foreground/70 mb-4">
          Det händer i Dalarna
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-border overflow-hidden">
              <Skeleton className="aspect-video w-full" />
              <div className="p-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!activities || activities.length === 0) return null;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-muted-foreground/70">
          Det händer i Dalarna
        </h2>
        <Link
          to="/explore"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        >
          Utforska fler
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {activities.map((a) => {
          if (a.kind === 'flea_market') {
            return (
              <Link
                key={a.id}
                to={a.href ?? `/loppis/${a.id}`}
                className="rounded-2xl border border-border overflow-hidden bg-card hover:bg-card/90 transition-colors"
                onClick={() => track('mypage_activity_clicked', { activity_id: a.id })}
              >
                <div className="aspect-video bg-muted overflow-hidden">
                  {a.coverImageUrl ? (
                    <img src={a.coverImageUrl} alt={a.title} className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground">Ingen bild</div>
                  )}
                </div>
                <div className="p-3 space-y-1">
                  <p className="text-sm font-medium text-foreground line-clamp-1">{a.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.startDate ? format(new Date(a.startDate), 'd MMM', { locale: sv }) : 'Datum saknas'}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-1">På {a.placeName ?? a.producerName ?? 'gården'}</p>
                </div>
              </Link>
            );
          }

          const uiTags = (a.outingTags || [])
            .map((t) => DB_TAG_TO_UI[t])
            .filter(Boolean) as OutingTag[];

          return (
            <div key={a.id} onClick={() => track('mypage_activity_clicked', { activity_id: a.id })}>
              {(() => {
                const timingLabel = formatEventTimingSummary(
                  (a.timingMode as any) || 'date_time',
                  a.startDate,
                  a.endDate,
                  a.outingTags || [],
                );
                return (
              <OutingCard
                id={a.id}
                title={a.title}
                coverImageUrl={a.coverImageUrl}
                dateLabel={timingLabel}
                distanceLabel={computeDistanceLabel(null, null, a.lat, a.lng)}
                badge={pickPrimaryBadge(uiTags)}
                saved={isFavorited('activity', a.id)}
                onToggleSave={() =>
                  toggleFavorite({
                    kind: 'activity',
                    entityId: a.id,
                    slug: a.id,
                    title: a.title,
                    imagePath: a.coverImageUrl ?? undefined,
                  })
                }
                startDate={a.startDate}
              />
                );
              })()}
            </div>
          );
        })}
      </div>
    </section>
  );
}
